/**
 * Helpers for AddUser handleSubmit to reduce cognitive complexity.
 */

import type { NavigateFunction } from 'react-router-dom';
import { authService } from '../../auth/authService';
import { ROLES } from '../../utils/roleUtils';
import {
  isAllowedSignatureUploadFile,
  SIGNATURE_UPLOAD_TYPE_ERROR,
} from '../../constants/constant';

type NotifyService =
  | {
      show: (opts: {
        title: string;
        message: string;
        type: 'success' | 'error' | 'warning' | 'info';
        duration?: number;
      }) => void;
    }
  | undefined;

export type AddUserFormValuesLike = {
  role: string;
  signature?: File | null;
};

export function notifyUserForm(
  uiNotificationService: NotifyService,
  success: boolean,
  successMessage: string,
  errorMessage: string
): void {
  if (!uiNotificationService) {
    return;
  }
  uiNotificationService.show({
    title: success ? 'Success' : 'Error',
    message: success ? successMessage : errorMessage,
    type: success ? 'success' : 'error',
    duration: success ? 3000 : 5000,
  });
}

export function validateRadiologistSignatureUpload(
  values: AddUserFormValuesLike,
  uiNotificationService: NotifyService
): boolean {
  if (values.role !== 'Radiologist') {
    return true;
  }
  if (!(values.signature instanceof File)) {
    return true;
  }
  if (isAllowedSignatureUploadFile(values.signature)) {
    return true;
  }
  if (uiNotificationService) {
    uiNotificationService.show({
      title: 'Invalid file',
      message: SIGNATURE_UPLOAD_TYPE_ERROR,
      type: 'error',
      duration: 5000,
    });
  }
  return false;
}

export function resolveAddUserFacilityId(
  role: string,
  locationFacilityId: string | undefined
): string | undefined {
  if (role === 'Radiologist' || role === 'Technician') {
    return authService.getFacilityId() || locationFacilityId || undefined;
  }
  return locationFacilityId;
}

export function resolveCurrentUserRoleForMapper(): string | undefined {
  const currentUser = authService.getUser();
  return (
    currentUser?.roles?.find(
      r =>
        r === 'ris-admin' ||
        r === ROLES.SUPER_ADMIN ||
        r === 'ris-radiologist' ||
        r === 'ris-technician'
    ) || undefined
  );
}

export function navigateAfterUserSave(
  navigate: NavigateFunction,
  facilityId: string | undefined,
  facilityName: string | undefined
): void {
  if (facilityId && facilityName) {
    navigate('/facility-view', {
      state: {
        facilityId,
        facilityName,
        activeTab: 'users',
      },
    });
    return;
  }
  navigate('/users');
}
